/**
 * Os clubes cadastrados, correndo em duas faixas como placas de estádio.
 *
 * As faixas andam em sentidos opostos e param quando o mouse passa por cima,
 * para quem quiser ler um nome ou clicar. Com movimento reduzido elas ficam
 * paradas e viram uma grade rolável de lado. A lista é a real (`/api/teams`),
 * na ordem em que o banco devolve; nada de clube de exemplo.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { queries, type TeamDto } from '@/lib/api';
import { TeamCrest } from '@/components/ui';
import { cn } from '@/lib/cn';

function Placa({ time }: { time: TeamDto }) {
  const titulos = time.titles ?? 0;
  return (
    <Link
      to={`/times/${time.slug}`}
      className="group flex shrink-0 items-center gap-3 rounded-xl border border-line bg-surface/70 py-3 pr-5 pl-3 transition-colors duration-[--duration-quick] hover:border-gold/50 hover:bg-surface-2"
    >
      <TeamCrest team={time} size="lg" />
      <span className="flex flex-col">
        <span className="ucc-display text-2xl whitespace-nowrap text-paper transition-colors group-hover:text-gold-ink">{time.name}</span>
        <span className="text-[0.6875rem] font-semibold tracking-wider whitespace-nowrap text-faint uppercase">
          {titulos > 0 ? (
            <span className="text-gold-ink">{titulos === 1 ? '1 título' : `${titulos} títulos`}</span>
          ) : (
            time.shortName
          )}
        </span>
      </span>
    </Link>
  );
}

function Faixa({ times, reversa }: { times: TeamDto[]; reversa?: boolean }) {
  // A lista vai duas vezes: quando a primeira cópia sai pela esquerda, a
  // segunda está exatamente onde ela começou, e o laço não tem emenda.
  const duracao = `${Math.max(30, times.length * 4)}s`;
  return (
    <div className="clubes-faixa overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_8%,#000_92%,transparent)]">
      <div
        className={cn('clubes-trilho flex w-max gap-3', reversa && 'clubes-trilho-reverso')}
        style={{ animationDuration: duracao }}
      >
        {[...times, ...times].map((t, i) => (
          <div key={`${t.id}-${i}`} aria-hidden={i >= times.length || undefined} className="flex">
            <Placa time={t} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ClubesDaPlataforma() {
  const { data } = useQuery(queries.teams());
  const times = data?.teams ?? [];
  if (times.length === 0) return null;

  // Campeões na frente de cada faixa: é quem o visitante mais reconhece.
  const ordenados = [...times].sort((a, b) => (b.titles ?? 0) - (a.titles ?? 0));
  const meio = Math.ceil(ordenados.length / 2);
  const faixas = ordenados.length >= 8 ? [ordenados.slice(0, meio), ordenados.slice(meio)] : [ordenados];

  return (
    <section className="relative z-10 overflow-hidden border-t border-line py-24" aria-labelledby="clubes-titulo">
      <div className="mx-auto mb-12 flex max-w-ucc flex-wrap items-end justify-between gap-6 px-6">
        <h2 id="clubes-titulo" className="ucc-display max-w-3xl text-[clamp(2.5rem,6vw,4.5rem)] text-paper">
          <span className="tabular text-gold-ink">{times.length}</span> clubes já
          <br />
          entraram em campo
        </h2>
        <Link
          to="/times"
          className="rounded-lg border border-line-strong px-5 py-3 text-sm font-semibold text-paper uppercase transition-colors hover:border-gold/50 hover:text-gold-ink"
        >
          Ver todos os clubes
        </Link>
      </div>
      <div className="flex flex-col gap-3">
        {faixas.map((f, i) => (
          <Faixa key={i} times={f} reversa={i === 1} />
        ))}
      </div>
    </section>
  );
}
